import { useState } from 'react'
import { X, Check, AlertCircle, Percent } from 'lucide-react'
import ParcelasEditor from '../common/ParcelasEditor'

const brl = v => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0)
const fmtMoney = v => (v == null || v === '') ? '' : new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)
const parseMoney = s => {
  if (s == null || s === '') return null
  const clean = String(s).replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.')
  const v = parseFloat(clean)
  return isNaN(v) ? null : v
}
const c2 = n => Math.round((n || 0) * 100) / 100

/**
 * Modal pra definir o parcelamento da comissão de compra (fluxo cavalo).
 * Salva { entrada_valor, entrada_data, parcelas: [{numero, valor, data}] }.
 * A soma (entrada + parcelas) precisa fechar o valor total da comissão.
 */
export default function ParcelamentoComissaoModal({ comissaoTotal, initial, onSalvar, onClose }) {
  const [entradaValorRaw, setEntradaValorRaw] = useState(
    initial?.entrada_valor != null ? fmtMoney(initial.entrada_valor) : ''
  )
  const [entradaData, setEntradaData] = useState(initial?.entrada_data || '')
  const [parcelas, setParcelas] = useState(
    (initial?.parcelas || []).map((p, i) => ({ numero: i + 1, valor: p.valor ?? null, data: p.data || '', travada: false }))
  )

  const entradaValor = parseMoney(entradaValorRaw) || 0
  const restante = c2(comissaoTotal - entradaValor)
  const somaParcelas = parcelas.reduce((s, p) => s + (p.valor || 0), 0)
  const totalDefinido = c2(entradaValor + somaParcelas)
  const diferenca = c2(totalDefinido - comissaoTotal)
  const fecha = Math.abs(diferenca) < 0.02
  const temParcela = parcelas.some(p => p.valor)

  const salvar = () => {
    onSalvar({
      entrada_valor: entradaValor > 0 ? entradaValor : null,
      entrada_data: entradaValor > 0 ? (entradaData || null) : null,
      parcelas: parcelas
        .filter(p => p.valor)
        .map((p, i) => ({ numero: i + 1, valor: p.valor, data: p.data || null })),
    })
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
              <Percent size={16} className="text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-gray-900">Parcelamento da comissão de compra</h2>
              <p className="text-xs text-gray-500">Aparece no contrato como tabela e nota promissória própria</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Total */}
          <div className="flex items-center justify-between rounded-xl bg-blue-50 border border-blue-200 px-4 py-3">
            <span className="text-sm text-blue-700 font-medium">Comissão de compra a parcelar</span>
            <span className="text-lg font-bold text-blue-900 tabular-nums">{brl(comissaoTotal)}</span>
          </div>

          {/* Entrada */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-500">Entrada (opcional)</label>
              <div className="relative flex items-center">
                <span className="absolute left-3 text-gray-400 text-sm pointer-events-none">R$</span>
                <input
                  type="text" value={entradaValorRaw}
                  onChange={e => setEntradaValorRaw(e.target.value)}
                  onBlur={() => { const v = parseMoney(entradaValorRaw); setEntradaValorRaw(v != null ? fmtMoney(v) : '') }}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-sm text-right tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-500">Data da entrada</label>
              <input
                type="date" value={entradaData} disabled={entradaValor <= 0}
                onChange={e => setEntradaData(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white disabled:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          {entradaValor > 0 && (
            <div className="flex items-center gap-2 text-xs text-blue-600 bg-white border border-blue-200 rounded-lg px-3 py-2">
              <span>Restante a parcelar:</span>
              <span className="font-bold tabular-nums">{brl(restante)}</span>
            </div>
          )}

          {/* Parcelas */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Parcelas</label>
            <ParcelasEditor
              restante={restante}
              parcelas={parcelas}
              onChange={setParcelas}
              refData={entradaData}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 flex items-center justify-between gap-3">
          <div className={`flex items-center gap-2 text-sm ${fecha ? 'text-green-700' : 'text-orange-600'}`}>
            {fecha ? <Check size={16} /> : <AlertCircle size={16} />}
            <span className="tabular-nums">
              Total definido: <strong>{brl(totalDefinido)}</strong>
              {!fecha && <span className="ml-1.5 font-normal text-xs">({diferenca > 0 ? '+' : ''}{brl(diferenca)} vs comissão)</span>}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors">
              Cancelar
            </button>
            <button
              onClick={salvar}
              disabled={!fecha || !temParcela}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 disabled:text-gray-400 text-white transition-colors"
            >
              <Check size={14} /> Salvar parcelamento
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
